<script setup lang="ts">
import {ref} from "vue";
import type {ScriptGoogleMaps} from "#components";
import {CustomClusterMarker} from "~/utils/maps/custom-cluster-marker";
import {useThrottleFn} from "@vueuse/core";
import type {SpecialistMarker} from "~/types/common";
import type {GeocoderResultLiteral, HydratedSpecialist} from "~/types/geocoded";

const MAP_ZOOM_LEVEL = 10;
const MAP_CHANGED_THROTTLE = 50;
const MAP_START_POSITION = {
  lat: 52.28732830049151,
  lng: 4.676883459500999,
};

type Props = {
  place: google.maps.GeocoderResult | GeocoderResultLiteral | undefined;
  selected: HydratedSpecialist | undefined;
  specialists: HydratedSpecialist[];
};

const {place, selected, specialists} = defineProps<Props>();
const emit = defineEmits<{
  clicked: [marker: SpecialistMarker];
  visible: [markers: SpecialistMarker[]];
}>();
const config = useRuntimeConfig();
const maps = ref<typeof ScriptGoogleMaps>();
const isLoaded = ref(false);
const center = ref(MAP_START_POSITION);
const mapOptions = {
  zoom: MAP_ZOOM_LEVEL,
  mapId: config.public.googleMapId || "",
  mapTypeId: "roadmap",
  mapTypeControl: false,
  streetViewControl: false,
  scrollwheel: true,
  zoomControl: true,
  scaleControl: true,
  cameraControl: false,
  fullscreenControl: false,
} satisfies google.maps.MapOptions;

async function handleReady() {
  const map = maps.value?.map;
  const importLibrary = maps.value?.importLibrary;
  const markerLib = await importLibrary("marker");
  const {MarkerUtils, MarkerClusterer, filterMarkersToPaddedViewport} =
    await import("@googlemaps/markerclusterer");

  async function createMarkers() {
    let markers = [];
    for (const specialist of specialists) {
      if (!specialist.address?.geocoding) continue;
      const {lat, lng} = specialist.address.geocoding.geometry.location;

      const markerImg = document.createElement("img");
      markerImg.src = "/img/maps/marker.svg";
      markerImg.style.position = "relative";
      markerImg.style.color = "white";

      const markerText = document.createElement("div");
      markerText.style.position = "absolute";
      markerText.style.width = "32px";
      markerText.style.textAlign = "center";
      markerText.style.fontWeight = "bold";
      markerText.style.top = "10px";

      const markerElement = document.createElement("div");
      markerElement.style.position = "relative";
      markerElement.append(markerImg);
      markerElement.append(markerText);

      const advancedMarkerElement = (await new markerLib.AdvancedMarkerElement({
        map: toRaw(map),
        position: {lat, lng},
        title: specialist.company,
        content: markerElement,
        gmpClickable: true,
      })) as SpecialistMarker;
      advancedMarkerElement.specialist = specialist;
      advancedMarkerElement.selected = false;
      advancedMarkerElement.addListener("gmp-click", () =>
        selectMarker(markers, advancedMarkerElement),
      );
      markers.push(advancedMarkerElement);
    }
    return markers;
  }

  function selectMarker(
    markers: SpecialistMarker[],
    selected: SpecialistMarker | undefined,
  ) {
    markers.forEach((marker: SpecialistMarker) => {
      if (MarkerUtils.isAdvancedMarker(marker) && marker.content) {
        const element = (marker.content as HTMLElement).querySelector("img");
        if (element) {
          element.src = `/img/maps/marker${marker === selected ? "_selected" : ""}.svg`;
        }
      }
    });
    if (selected) {
      emit("clicked", selected);
    }
  }

  const markers = await createMarkers();
  const clusterer = new MarkerClusterer({
    map: toRaw(map),
    markers: markers,
    renderer: new CustomClusterMarker(),
  });

  function visibleMarkers() {
    if (clusterer.getProjection()) {
      const visible = filterMarkersToPaddedViewport(
        clusterer.getMap() as google.maps.Map,
        clusterer.getProjection(),
        markers,
        -30,
      ) as SpecialistMarker[];
      emit("visible", visible);
    }
  }

  const throttledVisibleMarkers = useThrottleFn(
    visibleMarkers,
    MAP_CHANGED_THROTTLE,
  );

  toRaw(map).addListener("bounds_changed", () => throttledVisibleMarkers());

  watch(
    () => selected,
    () => {
      if (selected) {
        const marker = markers.find(
          (marker) => marker.specialist["@id"] === selected["@id"],
        );
        if (marker) {
          selectMarker(markers, marker);
        }
      } else {
        selectMarker(markers, undefined);
      }
    },
  );

  watch(
    () => place,
    () => {
      if (place) {
        map.panTo(place.geometry.location);
      }
      nextTick(() => throttledVisibleMarkers());
    },
    {immediate: true},
  );

  isLoaded.value = true;
}
</script>

<template>
  <ScriptGoogleMaps
    v-if="config.public.googleApiKey"
    ref="maps"
    :center="center"
    :api-key="config.public.googleApiKey"
    :map-options="mapOptions"
    :class="{invisible: !isLoaded}"
    @ready="handleReady" />
</template>
